import React, { useState, useEffect } from "react";
import { 
	BarChart3, 
	Users, 
	MessageSquare, 
	Activity,
	Mail,
	CheckCircle,
	XCircle,
	AlertCircle,
	Webhook
} from "lucide-react";
import WebhookAnalyticsReal from "./WebhookAnalyticsReal";
import DualUserAnalytics from "./DualUserAnalytics";

const AdminDashboardServerSide = () => {
	const [analytics, setAnalytics] = useState({
		totalUsers: 0,
		totalSessions: 0,
		totalMessages: 0,
		activeUsers: 0,
		messageDeliveryStats: {
			sent: 0,
			delivered: 0,
			read: 0,
			failed: 0
		},
		recentActivity: [],
		userEngagement: [],
		hourlyActivity: [],
		topics: []
	});
	const [loading, setLoading] = useState(true);
	const [progress, setProgress] = useState(0);
	const [progressMessage, setProgressMessage] = useState('');
	const [timeRange, setTimeRange] = useState('7d');
	const [activeTab, setActiveTab] = useState('overview');

	useEffect(() => {
		fetchAnalytics();
	}, [timeRange]);

	const getDaysFromTimeRange = (range) => {
		switch (range) {
			case '7d': return 7;
			case '30d': return 30;
			case '90d': return 90;
			default: return 7;
		}
	};

	const fetchAnalytics = async () => {
		try {
			setLoading(true);
			setProgress(0);
			setProgressMessage('🚀 Loading analytics from server...');
			
			// Server endpoint - hardcoded to Render backend
			const SERVER_URL = 'https://heyakua-whatsapp-bot-655772124305.us-central1.run.app';
			
			setProgress(25);
			setProgressMessage('📡 Connecting to analytics server...');
			
			const startTime = Date.now();
			const response = await fetch(`${SERVER_URL}/analytics?range=${timeRange}&refresh=true`, {
				method: 'GET',
				headers: {
					'Content-Type': 'application/json',
				},
			});

			if (!response.ok) {
				throw new Error(`Server error: ${response.status}`);
			}

			setProgress(50);
			setProgressMessage('⚡ Processing server response...');

			const result = await response.json();
			console.log('Analytics Response:', result); // Debug: Check if betaMetrics is included
			console.log('betaMetrics exists:', !!result.data?.betaMetrics);
			console.log('betaMetrics content:', result.data?.betaMetrics);
			const endTime = Date.now();
			const requestTime = endTime - startTime;

			if (result.success) {
				setProgress(75);
				setProgressMessage('🎯 Formatting analytics data...');
				
				// Transform server data to match the original component's expected format
				const serverData = result.data;
				
				// Generate hourly activity array (24 hours)
				const hourlyActivity = Array(24).fill(0);
				if (serverData.recentActivity) {
					serverData.recentActivity.forEach(activity => {
						if (activity.createdAt) {
							const hour = new Date(activity.timestamp || activity.createdAt).getHours();
							hourlyActivity[hour]++;
						}
					});
				}
				
				// Transform topics format
				const topics = [
					{ name: 'Short Sessions', count: Math.floor(serverData.totalSessions * 0.7) },
					{ name: 'Long Sessions', count: Math.floor(serverData.totalSessions * 0.3) }
				];

				setAnalytics({
					totalUsers: serverData.totalUsers || 0,
					totalSessions: serverData.totalSessions || 0,
					totalMessages: serverData.totalMessages || 0,
					activeUsers: serverData.activeUsers || 0,
					messageDeliveryStats: serverData.messageDeliveryStats || { sent: 0, delivered: 0, read: 0, failed: 0 },
					recentActivity: serverData.recentActivity || [],
					userEngagement: serverData.userEngagement || [],
					betaMetrics: serverData.betaMetrics, // ADD BETA METRICS!
					hourlyActivity,
					topics,
					performance: {
						...serverData.performance,
						requestTime,
						serverSide: true
					}
				});

				setProgress(100);
				setProgressMessage(`✅ Analytics loaded in ${requestTime}ms from server!`);
				
			} else {
				throw new Error(result.message || 'Failed to fetch analytics');
			}
			
		} catch (error) {
			console.error('Error fetching analytics:', error);
			setProgressMessage(`❌ Error loading analytics: ${error.message}`);
			
			// Fallback to empty data on error
			setAnalytics({
				totalUsers: 0,
				totalSessions: 0,
				totalMessages: 0,
				activeUsers: 0,
				messageDeliveryStats: { sent: 0, delivered: 0, read: 0, failed: 0 },
				recentActivity: [],
				userEngagement: [],
				hourlyActivity: Array(24).fill(0),
				topics: []
			});
		} finally {
			setLoading(false);
		}
	};

	// Rest of the component - using the exact same UI as the original AdminDashboard
	const StatCard = ({ title, value, icon: Icon, color, subtitle }) => (
		<div className="bg-white rounded-lg shadow-md p-6 border-l-4" style={{ borderLeftColor: color }}>
			<div className="flex items-center justify-between">
				<div>
					<p className="text-sm font-medium text-gray-600">{title}</p>
					<p className="text-2xl font-bold text-gray-900">{value}</p>
					{subtitle && <p className="text-xs text-gray-500 mt-1">{subtitle}</p>}
				</div>
				<Icon className={`h-8 w-8 ${color}`} />
			</div>
		</div>
	);

	return (
		<div className="min-h-screen bg-gray-50 p-6">
			<div className="max-w-7xl mx-auto">
				{/* Header */}
				<div className="mb-8">
					<div className="flex justify-between items-center">
						<div>
							<h1 className="text-3xl font-bold text-gray-900">Admin Dashboard</h1>
							<p className="text-gray-600 mt-2">Analytics and insights for your mental health platform</p>
						</div>
						<div className="flex space-x-4">
							<select
								value={timeRange}
								onChange={(e) => setTimeRange(e.target.value)}
								className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
							>
								<option value="7d">Last 7 days</option>
								<option value="30d">Last 30 days</option>
								<option value="90d">Last 90 days</option>
							</select>
							<button
								onClick={fetchAnalytics}
								className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
							>
								Refresh
							</button>
						</div>
					</div>
				</div>

				{/* Loading State */}
				{loading && (
					<div className="bg-white rounded-lg shadow-md p-6 mb-6">
						<div className="flex items-center space-x-4">
							<div className="flex-1">
								<div className="flex justify-between text-sm text-gray-600 mb-2">
									<span>{progressMessage}</span>
									<span>{progress}%</span>
								</div>
								<div className="w-full bg-gray-200 rounded-full h-2">
									<div 
										className="bg-blue-600 h-2 rounded-full transition-all duration-300"
										style={{ width: `${progress}%` }}
									></div>
								</div>
							</div>
						</div>
						{!loading && analytics.performance?.requestTime && (
							<p className="text-green-600 text-sm mt-2">
								⚡ Loaded in {analytics.performance.requestTime}ms from server
							</p>
						)}
					</div>
				)}

				{/* Tabs */}
				<div className="mb-6">
					<div className="border-b border-gray-200">
						<nav className="-mb-px flex space-x-8">
							{[
								{ id: 'overview', label: 'Overview', icon: BarChart3 },
								{ id: 'webhooks', label: 'Webhook Analytics', icon: Webhook },
								{ id: 'users', label: 'User Analytics', icon: Users }
							].map((tab) => (
								<button
									key={tab.id}
									onClick={() => setActiveTab(tab.id)}
									className={`flex items-center space-x-2 py-2 px-1 border-b-2 font-medium text-sm ${
										activeTab === tab.id
											? 'border-blue-500 text-blue-600'
											: 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
									}`}
								>
									<tab.icon className="h-4 w-4" />
									<span>{tab.label}</span>
								</button>
							))}
						</nav>
					</div>
				</div>

				{/* Content */}
				{activeTab === 'overview' && (
					<>
						{/* Stats Grid */}
						<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
							<StatCard
								title="Total Users"
								value={analytics.totalUsers.toLocaleString()}
								icon={Users}
								color="text-blue-600"
								subtitle="Registered users"
							/>
							<StatCard
								title="Active Users"
								value={analytics.activeUsers.toLocaleString()}
								icon={Activity}
								color="text-green-600"
								subtitle="Users with sessions"
							/>
							<StatCard
								title="Total Sessions"
								value={analytics.totalSessions.toLocaleString()}
								icon={MessageSquare}
								color="text-purple-600"
								subtitle="Therapy sessions"
							/>
							<StatCard
								title="Total Messages"
								value={analytics.totalMessages.toLocaleString()}
								icon={Mail}
								color="text-orange-600"
								subtitle="Messages exchanged"
							/>
						</div>


						{/* Recent Activity */}
						<div className="bg-white rounded-lg shadow-md p-6 mb-6">
							<h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Activity</h3>
							<div className="space-y-3">
								{analytics.recentActivity.slice(0, 10).map((activity, index) => (
									<div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
										<div className="flex items-center space-x-3">
											<div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
												<Users className="h-4 w-4 text-blue-600" />
											</div>
											<div>
												<p className="font-medium text-gray-900">{activity.userName}</p>
												<p className="text-sm text-gray-600">
													{activity.messageCount} messages • {new Date(activity.timestamp || activity.createdAt).toLocaleDateString()}
												</p>
											</div>
										</div>
										<span className="text-sm text-gray-500">
											{new Date(activity.timestamp || activity.createdAt).toLocaleTimeString()}
										</span>
									</div>
								))}
								{analytics.recentActivity.length === 0 && (
									<p className="text-gray-500 text-center py-4">No recent activity in the selected time range</p>
								)}
							</div>
						</div>

						{/* User Engagement */}
						<div className="bg-white rounded-lg shadow-md p-6 mb-6">
							<h3 className="text-lg font-semibold text-gray-900 mb-4">User Engagement</h3>
							<div className="space-y-3">
								{analytics.userEngagement.slice(0, 10).map((user, index) => (
									<div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
										<div className="flex items-center space-x-3">
											<div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
												<Activity className="h-4 w-4 text-green-600" />
											</div>
											<div>
												<p className="font-medium text-gray-900">{user.userName}</p>
												<p className="text-sm text-gray-600">
													Last active: {user.lastActivity ? new Date(user.lastActivity.seconds ? user.lastActivity.seconds * 1000 : user.lastActivity).toLocaleDateString() : 'Unknown'}
												</p>
											</div>
										</div>
										<span className={`px-2 py-1 rounded-full text-xs font-medium ${
											user.engagementLevel === 'High' ? 'bg-green-100 text-green-800' :
											user.engagementLevel === 'Medium' ? 'bg-yellow-100 text-yellow-800' :
											'bg-red-100 text-red-800'
										}`}>
											{user.engagementLevel}
										</span>
									</div>
								))}
								{analytics.userEngagement.length === 0 && (
									<p className="text-gray-500 text-center py-4">No user engagement data available</p>
								)}
							</div>
						</div>

					</>
				)}

				{activeTab === 'webhooks' && <WebhookAnalyticsReal />}

				{activeTab === 'users' && <DualUserAnalytics />}

			</div>
		</div>
	);
};

export default AdminDashboardServerSide;
